import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { X, Save, Key, Trash2, AlertTriangle, FileText, Download, Eye, Loader2, ShieldAlert } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const ALL_LOAN_TYPES = [
  { id: 'sba_loan', label: 'SBA Loan' },
  { id: 'term_loan_10yr', label: '10 Year Term Loan' },
  { id: 'term_loan_3_5yr', label: 'EZ Term Loan 3-5 Yr' },
  { id: 'term_loc_hybrid', label: 'Term Loan / LOC Hybrid' },
  { id: 'line_of_credit', label: 'Line of Credit' },
  { id: 'equipment_financing', label: 'Equipment Financing' },
  { id: 'merchant_processing', label: 'Merchant Processing' },
  { id: 'jumbo_term_loan', label: 'Jumbo Term Loan' },
  { id: 'transportation_factoring', label: 'Transportation Factoring' },
  { id: 'real_estate_financing', label: 'Real Estate Financing' },
  { id: 'debt_consulting', label: 'Debt Consolidation' },
  { id: 'foreclosure_bailout', label: 'Foreclosure Bailout' },
  { id: 'family_office', label: 'Family Office Programs' },
];

const formatBytes = (bytes) => {
  if (bytes === null || bytes === undefined) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Admin-facing viewer for a user's uploaded profile files (W9, voided check,
 * profile picture, company logo).  Files are streamed from the backend via
 * short-lived object URLs so authenticated download always works.
 */
const ProfileFilesSection = ({ userId }) => {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busyKind, setBusyKind] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(
        `${backendUrl}/api/users/${userId}/profile-files`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setItems(data?.files || []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load user files');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [userId, backendUrl, token]);

  useEffect(() => { load(); }, [load]);

  const fetchBlob = async (kind, download) => {
    setBusyKind(kind + (download ? ':dl' : ':view'));
    try {
      const resp = await axios.get(
        `${backendUrl}/api/users/${userId}/profile-file/${kind}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: download ? { download: 1 } : {},
          responseType: 'blob',
        },
      );
      // Best-effort filename recovery from the response headers.
      const disposition = resp.headers?.['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : `${kind}`;
      const url = URL.createObjectURL(resp.data);
      if (download) {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
      // Give the browser a moment to consume the blob before revoking.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Could not open file');
    } finally {
      setBusyKind(null);
    }
  };

  return (
    <Card data-testid="edit-user-profile-files-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center">
          <FileText className="h-4 w-4 mr-2" />
          Uploaded Profile Documents
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-slate-600 mb-3">
          Files this user has uploaded from their profile page (W9, voided check, etc.).
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading files…
          </div>
        ) : error ? (
          <Alert className="border-red-200 bg-red-50">
            <AlertDescription className="text-sm text-red-700">{error}</AlertDescription>
          </Alert>
        ) : !items || items.length === 0 ? (
          <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-md p-4 text-center">
            No profile documents on file for this user.
          </div>
        ) : (
          <div className="divide-y divide-slate-100 border border-slate-200 rounded-md">
            {items.map((it) => (
              <div key={it.kind} className="flex items-center gap-3 px-3 py-2.5" data-testid={`edit-user-file-row-${it.kind}`}>
                <div className="h-9 w-9 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
                  <FileText className="h-4 w-4 text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800">{it.label}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {it.filename}
                    {it.size ? ` · ${formatBytes(it.size)}` : ''}
                    {it.content_type ? ` · ${it.content_type}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => fetchBlob(it.kind, false)}
                    disabled={busyKind === `${it.kind}:view`}
                    data-testid={`edit-user-view-file-${it.kind}`}
                  >
                    {busyKind === `${it.kind}:view`
                      ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      : <Eye className="h-3.5 w-3.5 mr-1" />}
                    View
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => fetchBlob(it.kind, true)}
                    disabled={busyKind === `${it.kind}:dl`}
                    data-testid={`edit-user-download-file-${it.kind}`}
                  >
                    {busyKind === `${it.kind}:dl`
                      ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      : <Download className="h-3.5 w-3.5 mr-1" />}
                    Download
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const EditUserModal = ({ user, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company_name: '',
    roles: [],
    password: '',
    allowed_loan_types: [],
    commissions_enabled: false
  });
  const [loading, setLoading] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [accessStatus, setAccessStatus] = useState('active');
  const [savingAccess, setSavingAccess] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        company_name: user.company_name || '',
        roles: user.roles || [],
        password: '',
        allowed_loan_types: user.allowed_loan_types || [],
        commissions_enabled: user.commissions_enabled || false
      });
      // Default to "active" so nothing is accidentally restricted on modal open.
      setAccessStatus(user.access_status || 'active');
    }
  }, [user]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleRoleToggle = (role) => {
    setFormData(prev => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter(r => r !== role)
        : [...prev.roles, role]
    }));
  };

  const handleLoanTypeToggle = (typeId) => {
    setFormData(prev => ({
      ...prev,
      allowed_loan_types: prev.allowed_loan_types.includes(typeId)
        ? prev.allowed_loan_types.filter(t => t !== typeId)
        : [...prev.allowed_loan_types, typeId]
    }));
  };

  const handleAccessStatusChange = async (nextStatus) => {
    if (nextStatus === accessStatus) return;
    const messages = {
      restricted: `Set ${user?.name || 'this user'} to VIEW-ONLY (Restricted)?\n\nThey will still be able to log in and see the applications they've submitted plus their statuses, but every edit, upload, message, and download will be blocked.`,
      inactive: `Set ${user?.name || 'this user'} to INACTIVE?\n\nThey will NOT be able to log in at all. Existing sessions will be revoked on their next request.`,
      active: `Restore ${user?.name || 'this user'} to full ACTIVE access?`,
    };
    if (!window.confirm(messages[nextStatus])) return;
    setSavingAccess(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('auth_token');
      await axios.patch(
        `${backendUrl}/api/users/${user.id}/access-status`,
        { access_status: nextStatus },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setAccessStatus(nextStatus);
      const labels = { active: 'Active', restricted: 'Restricted (View-Only)', inactive: 'Inactive' };
      toast.success(`${user?.name || 'User'} is now ${labels[nextStatus]}. Change saved.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update access status');
    } finally {
      setSavingAccess(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('auth_token');

      await axios.put(
        `${backendUrl}/api/users/${user.id}`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      onSuccess();
      alert('✅ User updated successfully');
    } catch (error) {
      console.error('Error updating user:', error);
      alert('❌ Error updating user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (newPassword !== confirmPassword) {
      alert('❌ Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      alert('❌ Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('auth_token');

      await axios.post(
        `${backendUrl}/api/users/${user.id}/reset-password`,
        { new_password: newPassword },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setShowPasswordReset(false);
      setNewPassword('');
      setConfirmPassword('');
      alert('✅ Password reset successfully');
    } catch (error) {
      console.error('Error resetting password:', error);
      alert('❌ Error resetting password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!window.confirm(`Are you sure you want to delete ${user.name}? This will permanently remove the user and all their data. This action cannot be undone.`)) {
      return;
    }

    if (!window.confirm('This is your final warning. Are you absolutely sure you want to delete this user?')) {
      return;
    }

    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('auth_token');

      await axios.delete(
        `${backendUrl}/api/users/${user.id}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      onSuccess();
      onClose();
      alert('✅ User deleted successfully');
    } catch (error) {
      console.error('Error deleting user:', error);
      const errorMsg = error.response?.data?.detail || 'Please try again.';
      alert(`❌ Error deleting user: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const availableRoles = ['admin', 'broker', 'underwriter', 'jr_underwriter', 'borrower'];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-slate-800">Edit User Account</h2>
            <p className="text-slate-600">{user?.email}</p>
          </div>
          <button onClick={onClose} disabled={loading} className="h-9 w-9 flex items-center justify-center rounded-lg border-2 border-slate-300 bg-slate-100 text-slate-600 hover:bg-red-50 hover:border-red-400 hover:text-red-600 transition-all duration-200" data-testid="close-edit-user-modal">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* User Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">User Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    disabled={loading}
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    disabled={loading}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="company_name">Company Name (Optional)</Label>
                <Input
                  id="company_name"
                  value={formData.company_name}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  disabled={loading}
                />
              </div>
            </CardContent>
          </Card>

          {/* Roles Management */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">User Roles</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <p className="text-sm text-slate-600">Select the roles this user should have:</p>
                <div className="grid grid-cols-2 gap-3">
                  {availableRoles.map((role) => (
                    <div
                      key={role}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        formData.roles.includes(role)
                          ? 'bg-slate-100 border-slate-400'
                          : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => handleRoleToggle(role)}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium capitalize">{role === 'jr_underwriter' ? 'Jr Underwriter' : role}</span>
                        {formData.roles.includes(role) && (
                          <Badge className="bg-teal-600 text-white">Active</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="text-sm text-slate-500">
                  Current roles: {formData.roles.length > 0 ? formData.roles.join(', ') : 'None'}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Allowed Loan Products - shown when broker or underwriter role is selected */}
          {(formData.roles.includes('broker') || formData.roles.includes('underwriter')) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Allowed Loan Products</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-3">
                  Select the loan products this {formData.roles.includes('underwriter') ? 'underwriter can review' : 'broker can submit applications for'}:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {ALL_LOAN_TYPES.map((type) => (
                    <div
                      key={type.id}
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                        formData.allowed_loan_types.includes(type.id)
                          ? 'bg-teal-50 border-teal-400'
                          : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => handleLoanTypeToggle(type.id)}
                      data-testid={`edit-toggle-loan-${type.id}`}
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        formData.allowed_loan_types.includes(type.id) ? 'bg-teal-600 border-teal-600' : 'border-slate-300'
                      }`}>
                        {formData.allowed_loan_types.includes(type.id) && (
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <span className="text-sm font-medium">{type.label}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  {formData.allowed_loan_types.filter(id => ALL_LOAN_TYPES.some(t => t.id === id)).length} of {ALL_LOAN_TYPES.length} products enabled
                </p>
              </CardContent>
            </Card>
          )}

          {/* Commissions Access - shown when broker role is selected */}
          {formData.roles.includes('broker') && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Commissions Portal Access</CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-colors ${
                    formData.commissions_enabled
                      ? 'bg-emerald-50 border-emerald-400'
                      : 'bg-white border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => handleInputChange('commissions_enabled', !formData.commissions_enabled)}
                  data-testid="edit-toggle-commissions"
                >
                  <div>
                    <p className="font-medium text-slate-800">Enable Commissions Tab</p>
                    <p className="text-sm text-slate-500">Allow this broker to view and confirm commission records</p>
                  </div>
                  <div className={`w-12 h-7 rounded-full transition-colors relative ${
                    formData.commissions_enabled ? 'bg-emerald-500' : 'bg-slate-300'
                  }`}>
                    <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${
                      formData.commissions_enabled ? 'translate-x-5' : 'translate-x-0.5'
                    }`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Uploaded Profile Documents (W9, voided check, etc.) */}
          {user?.id && <ProfileFilesSection userId={user.id} />}

          {/* Access Status — Active / Restricted / Inactive */}
          <Card data-testid="edit-user-access-status-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center">
                <ShieldAlert className="h-4 w-4 mr-2" />
                Access Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-slate-600">
                Control this user&apos;s ability to log in and take actions. New users are always <strong>Active</strong> by default — nothing is changed until you explicitly pick another option here.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {[
                  { id: 'active', label: 'Active', desc: 'Full access — the normal state.', tone: 'border-emerald-200 bg-emerald-50 text-emerald-900' },
                  { id: 'restricted', label: 'Restricted (View-Only)', desc: 'Can log in and see submitted applications + statuses only. No edits, uploads, messages, or downloads.', tone: 'border-amber-200 bg-amber-50 text-amber-900' },
                  { id: 'inactive', label: 'Inactive', desc: 'Cannot log in at all. Existing sessions are revoked on next request.', tone: 'border-red-200 bg-red-50 text-red-900' },
                ].map((opt) => {
                  const selected = accessStatus === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => handleAccessStatusChange(opt.id)}
                      disabled={savingAccess}
                      className={`text-left p-3 rounded-lg border-2 transition-all ${selected ? `${opt.tone} border-current ring-2 ring-current ring-offset-1` : 'border-slate-200 hover:border-slate-300 bg-white text-slate-700'} ${savingAccess ? 'opacity-60 cursor-wait' : ''}`}
                      data-testid={`access-status-btn-${opt.id}`}
                    >
                      <div className="font-semibold text-sm flex items-center gap-1.5">
                        {selected && <span className="inline-block w-2 h-2 rounded-full bg-current" />}
                        {opt.label}
                      </div>
                      <div className={`text-xs mt-1 ${selected ? '' : 'text-slate-500'}`}>{opt.desc}</div>
                    </button>
                  );
                })}
              </div>
              {accessStatus === 'restricted' && (
                <div className="text-xs bg-amber-50 border border-amber-200 rounded-md p-2.5 text-amber-800">
                  This user will see the message: <em>&quot;Your access has been restricted to view only as you are no longer in active status.&quot;</em> whenever they try to take an action.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Password Reset */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center">
                <Key className="h-4 w-4 mr-2" />
                Password Management
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!showPasswordReset ? (
                <div className="space-y-3">
                  <p className="text-sm text-slate-600">Reset the user's password to a new value.</p>
                  <Button
                    variant="outline"
                    onClick={() => setShowPasswordReset(true)}
                    disabled={loading}
                  >
                    <Key className="h-4 w-4 mr-2" />
                    Reset Password
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="newPassword">New Password</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password (min 6 characters)"
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      disabled={loading}
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button
                      onClick={handlePasswordReset}
                      disabled={loading || !newPassword || !confirmPassword}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {loading ? 'Resetting...' : 'Reset Password'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowPasswordReset(false);
                        setNewPassword('');
                        setConfirmPassword('');
                      }}
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Danger Zone */}
          <Card className="border-red-200">
            <CardHeader>
              <CardTitle className="text-lg text-red-600 flex items-center">
                <AlertTriangle className="h-4 w-4 mr-2" />
                Danger Zone
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Alert className="border-red-200 bg-red-50">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Deleting a user will permanently remove their account and all associated data including applications, messages, and documents. This action cannot be undone.
                </AlertDescription>
              </Alert>
              <Button
                variant="outline"
                onClick={handleDeleteUser}
                disabled={loading}
                className="mt-4 text-red-600 border-red-200 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete User Account
              </Button>
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t bg-slate-50">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={loading || !formData.name || !formData.email}
            className="bg-teal-600 hover:bg-teal-700"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditUserModal;